/**
 * PropertiesEnhanced — Phase 04 redesign
 * 3-column layout: building tree | room grid by floor | room detail drawer
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Icons,
  KPI, KPIStrip,
  Drawer, DrawerBody, DrawerSection, DrawerFooter, KVList,
  EmptyState, Skeleton, QueryErrorBanner,
  CurrencyInput,
} from '@/shared/ui';
import api from '../utils/api';
import RoomFurniturePanel from '../components/properties/RoomFurniturePanel';
import ServiceConfigManager, { defaultServicesConfig } from '../components/admin/ServiceConfigManager';

const intlLocale = (lang) => (lang === 'en' ? 'en-US' : 'vi-VN');

// FastAPI 422 returns detail as array of {loc, msg, input, url}; extract a readable string.
const apiErrMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(e => `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}`).join(' | ');
  if (typeof d === 'string') return d;
  return fallback;
};

const VND = (n, lang = 'vi') =>
  new Intl.NumberFormat(intlLocale(lang), { style: 'currency', currency: 'VND' }).format(n ?? 0);

// ── room status pill ──────────────────────────────────────────────────────────
function RoomStatusPill({ status, pendingDate }) {
  const { t, i18n } = useTranslation('properties');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const meta = {
    available:   { label: t('enhanced.roomStatus.available'),   cls: 'text-green-700 bg-green-50  ring-green-200' },
    occupied:    { label: t('enhanced.roomStatus.occupied'),    cls: 'text-teal-700  bg-teal-50   ring-teal-200'  },
    maintenance: { label: t('enhanced.roomStatus.maintenance'), cls: 'text-amber-700 bg-amber-50  ring-amber-200' },
    reserved:    { label: t('enhanced.roomStatus.reserved'),    cls: 'text-violet-700 bg-violet-50 ring-violet-200'},
    pending:     { label: t('enhanced.roomStatus.pending'),     cls: 'text-blue-700  bg-blue-50   ring-blue-200'  },
  };
  let m = meta[status] ?? { label: status, cls: 'text-stone-600 bg-stone-100 ring-stone-200' };
  // For "pending" (upcoming contract), append the start date as dd/MM.
  if (status === 'pending' && pendingDate instanceof Date) {
    const dd = String(pendingDate.getDate()).padStart(2, '0');
    const mm = String(pendingDate.getMonth() + 1).padStart(2, '0');
    m = { ...m, label: `${m.label} · ${dd}/${mm}` };
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ── building list (left column) ───────────────────────────────────────────────
function BuildingList({ buildings, selected, onSelect, loading }) {
  const { t } = useTranslation('properties');
  if (loading) {
    return (
      <div className="space-y-1.5 p-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-14 bg-stone-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }
  if (buildings.length === 0) {
    return (
      <p className="px-4 py-6 text-[13px] text-stone-400 text-center">{t('enhanced.buildingEmpty')}</p>
    );
  }
  return (
    <div className="space-y-0.5 p-2">
      {buildings.map(b => {
        const isSelected = selected?.id === b.id;
        // Prefer live room_count (from subquery); fall back to configured total_rooms
        const total    = (b.room_count ?? b.total_rooms ?? 0);
        const occupied = (b.occupied_rooms ?? 0);
        return (
          <button
            key={b.id}
            onClick={() => onSelect(b)}
            className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
              isSelected
                ? 'bg-teal-50 border border-teal-200'
                : 'hover:bg-stone-50 border border-transparent'
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
              isSelected ? 'bg-teal-600 text-white' : 'bg-stone-100 text-stone-500'
            }`}>
              <Icons.Building size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[13px] font-semibold truncate ${isSelected ? 'text-teal-800' : 'text-stone-800'}`}>
                {b.name}
              </p>
              <p className="text-[11px] text-stone-400 mt-0.5">
                {t('enhanced.occupiedSummary', { occupied, total })}
              </p>
              {total > 0 && (
                <div className="mt-1.5 h-1 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full transition-all"
                    style={{ width: `${(occupied / total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── room card ─────────────────────────────────────────────────────────────────
function RoomCard({ room, contractInfo, upcomingInfo, selected, onClick, bulkMode = false, bulkSelected = false, onBulkToggle }) {
  const { t, i18n } = useTranslation('properties');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  // Contract data is authoritative over room.status, which can stay stale
  // (e.g. 'occupied' after the only contract was terminated). An upcoming
  // contract always wins over a stale 'occupied' status.
  const hasCurrent  = !!contractInfo;
  const hasUpcoming = !!upcomingInfo;
  const isOccupied  = hasCurrent || (room.status === 'occupied' && !hasUpcoming);
  const isPending   = !hasCurrent && hasUpcoming;
  const displayStatus = isOccupied ? 'occupied' : isPending ? 'pending' : room.status;
  const tenant = contractInfo?.tenantNames || (isPending ? upcomingInfo.tenantNames : null);
  const price  = contractInfo?.contractPrice || upcomingInfo?.contractPrice || room.monthly_rent;
  // Surface color: teal when occupied, blue tint when pending, stone otherwise.
  const headerBg = isOccupied ? 'bg-teal-50/60' : isPending ? 'bg-blue-50/60' : 'bg-stone-50';

  return (
    <button
      onClick={bulkMode ? onBulkToggle : onClick}
      className={`w-full text-left rounded-xl border transition-all ${
        bulkMode
          ? bulkSelected
            ? 'border-teal-500 bg-teal-50/40 ring-2 ring-teal-500/30'
            : 'border-stone-200 bg-white hover:border-teal-200'
          : selected
            ? 'border-teal-500 bg-teal-50/30 ring-2 ring-teal-500/20'
            : 'border-stone-200 bg-white hover:border-teal-300 hover:shadow-sm'
      }`}
    >
      {/* Card header */}
      <div className={`px-3 py-2 flex items-center justify-between rounded-t-xl ${headerBg}`}>
        <div className="flex items-center gap-2">
          {bulkMode && (
            <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
              bulkSelected ? 'bg-teal-600 border-teal-600' : 'border-stone-400 bg-white'
            }`}>
              {bulkSelected && <Icons.Check size={10} className="text-white" />}
            </span>
          )}
          <span className="text-[13px] font-bold text-stone-900">{room.room_number}</span>
        </div>
        <RoomStatusPill status={displayStatus} pendingDate={isPending ? upcomingInfo.startDate : null} />
      </div>

      {/* Card body */}
      <div className="px-3 py-2.5 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-stone-400 uppercase tracking-wider font-medium">{t('enhanced.priceLabel')}</span>
          <span className="text-[13px] font-semibold text-teal-700">
            {VND(price, lang)}
          </span>
        </div>
        {(isOccupied && contractInfo) || (isPending && upcomingInfo) ? (
          <>
            <div className="flex items-center gap-1.5">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                isPending ? 'bg-blue-200' : 'bg-teal-200'
              }`}>
                <span className={`font-bold text-[10px] uppercase ${
                  isPending ? 'text-blue-700' : 'text-teal-700'
                }`}>
                  {(tenant || '?').charAt(0)}
                </span>
              </div>
              <p className="text-[12px] text-stone-700 font-medium truncate flex-1">{tenant}</p>
            </div>
            {/* End-date strip — red when within 30 days, stone otherwise */}
            {(() => {
              const endDate = isOccupied ? contractInfo?.endDate : upcomingInfo?.endDate;
              if (!endDate) return null;
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const daysLeft = Math.ceil((endDate - today) / 86400000);
              const expiring = daysLeft >= 0 && daysLeft <= 30;
              const dd = String(endDate.getDate()).padStart(2, '0');
              const mm = String(endDate.getMonth() + 1).padStart(2, '0');
              const yy = endDate.getFullYear();
              return (
                <div className={`flex items-center justify-between text-[11px] ${expiring ? 'text-red-600 font-semibold' : 'text-stone-400'}`}>
                  <span>{t('enhanced.endDateLabel')}</span>
                  <span className="num">
                    {dd}/{mm}/{yy}
                    {expiring && daysLeft >= 0 && (
                      <span className="ml-1">· {t('enhanced.daysLeft', { count: daysLeft })}</span>
                    )}
                  </span>
                </div>
              );
            })()}
          </>
        ) : (
          <p className="text-[11px] text-stone-400 text-center py-0.5">{t('enhanced.emptyRoom')}</p>
        )}
        {room.floor && (
          <p className="text-[11px] text-stone-400">{t('enhanced.floorLabel', { floor: room.floor })}</p>
        )}
      </div>
    </button>
  );
}

// ── room detail drawer ────────────────────────────────────────────────────────
function RoomDetailDrawer({ room, building, onClose, onEdit, onDelete }) {
  const { t, i18n } = useTranslation('properties');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const locale = intlLocale(lang);
  const [detailTab, setDetailTab] = useState('info');

  const { data: roomDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ['room-details', room?.id],
    queryFn: () => api.get(`/api/properties/rooms/${room.id}/details`).then(r => r.data),
    enabled: !!room,
    staleTime: 2 * 60 * 1000,
  });

  const { data: furniture = [], isLoading: furnLoading } = useQuery({
    queryKey: ['room-furniture', room?.id],
    queryFn: () =>
      api.get(`/api/properties/rooms/${room.id}/furniture`).then(r => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.furniture ?? []);
      }),
    enabled: !!room && detailTab === 'furniture',
    staleTime: 5 * 60 * 1000,
  });

  if (!room) return null;

  // Classify the room's current contract by date — a future-signed contract
  // should render as "pending" (Sắp thuê · dd/MM), not "occupied".
  const contract = roomDetails?.current_contract;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const contractStart = contract?.start_date ? new Date(contract.start_date) : null;
  const isPendingContract = !!contract && !!contractStart && contractStart > today;
  const displayStatus = contract
    ? (isPendingContract ? 'pending' : 'occupied')
    : (roomDetails?.status || room.status);

  const DETAIL_TABS = [
    { key: 'info',      label: t('enhanced.tab.info') },
    { key: 'furniture', label: t('enhanced.tab.furniture') },
  ];

  return (
    <>
      <DrawerBody>
        {/* Status strip */}
        <DrawerSection>
          <div className="flex items-center gap-3">
            <RoomStatusPill status={displayStatus} pendingDate={isPendingContract ? contractStart : null} />
            <span className="text-[13px] text-stone-500">
              {building?.name}{room.floor ? ` · ${t('enhanced.floorLabel', { floor: room.floor })}` : ''}
            </span>
          </div>
        </DrawerSection>

        {/* Tab strip */}
        <div className="flex items-center gap-1 px-5 border-b border-stone-100">
          {DETAIL_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setDetailTab(tab.key)}
              className={`px-3 py-2 text-[13px] font-medium border-b-2 transition-colors -mb-px ${
                detailTab === tab.key
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {detailTab === 'info' && (
          <>
            {/* Basic info */}
            <DrawerSection title={t('enhanced.detail.section.roomInfo')}>
              {detailsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <div key={i} className="h-6 bg-stone-100 rounded animate-pulse" />)}
                </div>
              ) : (
                <KVList items={[
                  [t('enhanced.detail.kv.roomNumber'), roomDetails?.room_number || room.room_number],
                  [t('enhanced.detail.kv.floor'),      roomDetails?.floor ?? room.floor ?? '—'],
                  [t('enhanced.detail.kv.area'),       roomDetails?.area ? `${roomDetails.area} m²` : '—'],
                  [t('enhanced.detail.kv.capacity'),   roomDetails?.capacity
                    ? t('enhanced.detail.kv.capacityValue', { count: roomDetails.capacity })
                    : '—'],
                  [t('enhanced.detail.kv.monthlyRent'),VND(roomDetails?.monthly_rent || room.monthly_rent, lang)],
                ]} />
              )}
            </DrawerSection>

            {/* Tenant / contract */}
            {contract ? (
              <>
                <DrawerSection title={t('enhanced.detail.section.contract')}>
                  <KVList items={[
                    [t('enhanced.detail.kv.tenant'),
                      contract.renter_name
                        || contract.renters?.map(r => `${r.first_name || ''} ${r.last_name || ''}`.trim()).filter(Boolean).join(', ')
                        || contract.occupants?.map(r => `${r.first_name || ''} ${r.last_name || ''}`.trim()).filter(Boolean).join(', ')
                        || roomDetails?.current_tenants?.map(r => r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim()).filter(Boolean).join(', ')
                        || roomDetails?.current_tenant?.full_name
                        || '—'],
                    [t('enhanced.detail.kv.contractNumber'), contract.contract_number || `#${contract.id}`],
                    [t('enhanced.detail.kv.startDate'), contract.start_date ? new Date(contract.start_date).toLocaleDateString(locale) : '—'],
                    [t('enhanced.detail.kv.endDate'),   contract.end_date ? new Date(contract.end_date).toLocaleDateString(locale) : '—'],
                    [t('enhanced.detail.kv.rent'),      VND(contract.monthly_rent, lang)],
                    ...(contract.deposit_amount ? [['Đặt cọc', VND(contract.deposit_amount, lang)]] : []),
                    ...(contract.payment_due_day ? [['Ngày đóng tiền', `Ngày ${contract.payment_due_day} hàng tháng`]] : []),
                    ...(contract.occupants_count > 0 ? [['Số người ở', `${contract.occupants_count} người`]] : []),
                  ]} />
                </DrawerSection>

                {/* Services / fees */}
                {(() => {
                  const services = contract.services_config;
                  if (!Array.isArray(services) || services.length === 0) return null;
                  const active = services.filter(s => s.is_active !== false);
                  if (active.length === 0) return null;

                  const unitLabel = (s) => {
                    if (s.type === 'metered') return `/${s.unit || 'đơn vị'}`;
                    if (s.type === 'fixed_per_person') return '/người';
                    return '/phòng';
                  };

                  return (
                    <DrawerSection title="Dịch vụ & Phí">
                      <div className="space-y-0">
                        {active.map((s, i) => (
                          <div key={i} className="flex items-baseline justify-between py-1.5 border-b border-stone-50 last:border-0">
                            <div className="flex-1 min-w-0 mr-3">
                              <span className="text-[13px] text-stone-700">{s.name}</span>
                              {s.type === 'metered' && (
                                <span className="ml-1.5 text-[11px] text-stone-400">theo đồng hồ</span>
                              )}
                            </div>
                            {s.is_included ? (
                              <span className="text-[12px] text-emerald-600 font-medium">Miễn phí</span>
                            ) : (
                              <span className="text-[12px] text-stone-700 tabular-nums whitespace-nowrap">
                                {VND(s.unit_price, lang)}<span className="text-stone-400 text-[11px]">{unitLabel(s)}</span>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </DrawerSection>
                  );
                })()}
              </>
            ) : (
              <DrawerSection title={t('enhanced.detail.section.noContract')}>
                <p className="text-[13px] text-stone-400">{t('enhanced.detail.emptyContract')}</p>
              </DrawerSection>
            )}

            {/* Recent invoices */}
            {roomDetails?.recent_invoices?.length > 0 && (
              <DrawerSection title={t('enhanced.detail.section.recentInvoices')}>
                <div className="space-y-1">
                  {roomDetails.recent_invoices.slice(0, 3).map((inv, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0">
                      <span className="text-[12px] text-stone-600">{inv.month}</span>
                      <span className="text-[12px] font-medium text-stone-900">{VND(inv.amount, lang)}</span>
                    </div>
                  ))}
                </div>
              </DrawerSection>
            )}
          </>
        )}

        {detailTab === 'furniture' && (
          <DrawerSection title={t('enhanced.detail.section.furniture', { count: furniture.length })}>
            <RoomFurniturePanel roomId={room.id} buildingId={room.building_id || building?.id} />
          </DrawerSection>
        )}
      </DrawerBody>

      <DrawerFooter>
        <a
          href="/manager/contracts"
          className="flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
        >
          <Icons.FileCheck size={14} />
          {t('enhanced.detail.action.contracts')}
        </a>
        {onEdit && (
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-teal-200 text-teal-700 text-[13px] font-medium hover:bg-teal-50 transition-colors"
          >
            <Icons.Edit size={14} />
            {t('enhanced.detail.action.edit')}
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="h-9 px-3 rounded-lg border border-red-200 text-red-600 text-[13px] font-medium hover:bg-red-50 transition-colors"
          >
            <Icons.Trash size={14} />
          </button>
        )}
        <button
          onClick={onClose}
          className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors"
        >
          {t('enhanced.detail.action.close')}
        </button>
      </DrawerFooter>
    </>
  );
}

// ── building form drawer ──────────────────────────────────────────────────────
function BuildingFormDrawer({ open, building, onClose, onSaved, initialTab = 'info' }) {
  const { t } = useTranslation(['properties', 'settings']);
  const qc = useQueryClient();
  const isEdit = !!building;
  const [tab, setTab] = useState(initialTab);

  const parseServices = (raw) => {
    if (!raw) return [...defaultServicesConfig];
    if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return [...defaultServicesConfig]; } }
    return Array.isArray(raw) && raw.length > 0 ? raw : [...defaultServicesConfig];
  };

  const [name, setName]               = useState(building?.name || '');
  const [address, setAddress]         = useState(building?.address || '');
  const [floors, setFloors]           = useState(building?.floors || 1);
  const [totalRooms, setTotalRooms]   = useState(building?.total_rooms ?? building?.room_count ?? 0);
  const [servicesConfig, setServicesConfig] = useState(() => parseServices(building?.services_config));
  const [billingDay, setBillingDay]   = useState(building?.billing_day ?? 5);
  const [paymentMethod, setPaymentMethod] = useState(building?.payment_method ?? 'pay_first');
  const [graceDays, setGraceDays]     = useState(building?.billing_grace_days ?? 3);

  // Re-sync whenever the drawer opens or the target building changes
  useEffect(() => {
    if (open) {
      setName(building?.name || '');
      setAddress(building?.address || '');
      setFloors(building?.floors || 1);
      setTotalRooms(building?.total_rooms ?? building?.room_count ?? 0);
      setServicesConfig(parseServices(building?.services_config));
      setBillingDay(building?.billing_day ?? 5);
      setPaymentMethod(building?.payment_method ?? 'pay_first');
      setGraceDays(building?.billing_grace_days ?? 3);
      setTab(initialTab);
    }
  }, [open, building?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const labelCls = 'block text-xs font-medium text-stone-600 mb-1.5';

  const save = useMutation({
    mutationFn: (data) =>
      isEdit
        ? api.put(`/api/properties/buildings/${building.id}`, data).then(r => r.data)
        : api.post('/api/properties/buildings', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buildings'] });
      qc.invalidateQueries({ queryKey: ['property-stats'] });
      toast.success(isEdit ? t('properties:enhanced.toast.buildingUpdated') : t('properties:enhanced.toast.buildingCreated'));
      onSaved?.();
      onClose();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}`).join(' | ')
        : (typeof detail === 'string' ? detail : t('properties:enhanced.toast.saveFailed'));
      toast.error(msg || t('properties:enhanced.toast.saveFailed'));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    save.mutate({
      name,
      address,
      floors: parseInt(floors) || 1,
      total_rooms: parseInt(totalRooms) || 0,
      billing_day: billingDay,
      payment_method: paymentMethod,
      billing_grace_days: graceDays,
      services_config: servicesConfig,
    });
  };

  const TABS = [
    { key: 'info',     label: t('properties:enhanced.buildingForm.tab.info') },
    { key: 'services', label: t('properties:enhanced.buildingForm.tab.services') },
    { key: 'billing',  label: t('properties:enhanced.buildingForm.tab.billing') },
  ];

  return (
    <Drawer open={open} onClose={onClose}
      title={isEdit ? t('properties:enhanced.buildingForm.editTitle') : t('properties:enhanced.buildingForm.addTitle')}
      width={560}>
      <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
        {/* Tab strip */}
        <div className="flex items-center gap-0 px-5 border-b border-stone-100 flex-shrink-0">
          {TABS.map(tb => (
            <button key={tb.key} type="button" onClick={() => setTab(tb.key)}
              className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors -mb-px ${
                tab === tb.key ? 'border-teal-600 text-teal-700' : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}>
              {tb.label}
            </button>
          ))}
        </div>

        <DrawerBody>
          {/* Tab: Basic info */}
          {tab === 'info' && (
            <DrawerSection title={t('properties:enhanced.buildingForm.section.info')}>
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>{t('properties:enhanced.buildingForm.field.name')} *</label>
                  <input required className={fieldCls} value={name} onChange={e => setName(e.target.value)} placeholder="Sunrise Apt." />
                </div>
                <div>
                  <label className={labelCls}>{t('properties:enhanced.buildingForm.field.address')}</label>
                  <input className={fieldCls} value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Nguyễn Trãi, Q.7" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>{t('properties:enhanced.buildingForm.field.floors')}</label>
                    <input type="number" min="1" max="50" className={fieldCls} value={floors} onChange={e => setFloors(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t('properties:enhanced.buildingForm.field.totalRooms')}</label>
                    <input type="number" min="0" className={fieldCls} value={totalRooms} onChange={e => setTotalRooms(e.target.value)} />
                  </div>
                </div>
              </div>
            </DrawerSection>
          )}

          {/* Tab: Services pricing */}
          {tab === 'services' && (
            <DrawerSection title={t('properties:enhanced.buildingForm.section.services')}>
              <p className="text-[11px] text-stone-400 mb-3">
                {t('properties:enhanced.buildingForm.servicesHint')}
              </p>
              <ServiceConfigManager
                services={servicesConfig}
                onChange={setServicesConfig}
                title={t('properties:enhanced.buildingForm.section.services')}
              />
            </DrawerSection>
          )}

          {/* Tab: Billing settings */}
          {tab === 'billing' && (
            <DrawerSection title={t('properties:enhanced.buildingForm.section.billing')}>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>{t('properties:enhanced.buildingForm.field.paymentMethod')}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['pay_first', 'pay_later'].map(m => (
                      <button
                        key={m} type="button"
                        onClick={() => setPaymentMethod(m)}
                        className={`flex flex-col items-start p-3 rounded-lg border text-left transition-colors ${
                          paymentMethod === m
                            ? 'border-teal-500 bg-teal-50 text-teal-800'
                            : 'border-stone-200 hover:border-stone-300 text-stone-700'
                        }`}
                      >
                        <span className="text-[13px] font-semibold">
                          {t(`properties:enhanced.buildingForm.paymentMethod.${m}`)}
                        </span>
                        <span className="text-[11px] mt-0.5 text-stone-500">
                          {t(`properties:enhanced.buildingForm.paymentMethodDesc.${m}`)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>{t('properties:enhanced.buildingForm.field.billingDay')}</label>
                    <input
                      type="number" min="1" max="28"
                      className={fieldCls}
                      value={billingDay}
                      onChange={e => setBillingDay(parseInt(e.target.value) || 1)}
                    />
                    <p className="text-[11px] text-stone-400 mt-1">
                      {t('properties:enhanced.buildingForm.billingDayHint')}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>{t('properties:enhanced.buildingForm.field.graceDays')}</label>
                    <input
                      type="number" min="0" max="15"
                      className={fieldCls}
                      value={graceDays}
                      onChange={e => setGraceDays(parseInt(e.target.value) || 0)}
                    />
                    <p className="text-[11px] text-stone-400 mt-1">
                      {t('properties:enhanced.buildingForm.graceDaysHint')}
                    </p>
                  </div>
                </div>
                {/* Preview */}
                <div className="p-3 bg-stone-50 border border-stone-200 rounded-lg text-[12px] text-stone-600 space-y-1">
                  <p className="font-semibold text-stone-700 mb-1">{t('properties:enhanced.buildingForm.billingPreviewTitle')}</p>
                  <p>
                    {paymentMethod === 'pay_first'
                      ? t('properties:enhanced.buildingForm.billingPreview.pay_first', { day: billingDay })
                      : t('properties:enhanced.buildingForm.billingPreview.pay_later', { day: billingDay })}
                  </p>
                  <p>{t('properties:enhanced.buildingForm.billingPreview.deadline', { day: Math.min(billingDay + graceDays, 28) })}</p>
                </div>
              </div>
            </DrawerSection>
          )}
        </DrawerBody>

        <DrawerFooter>
          <button type="button" onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            {t('properties:enhanced.buildingForm.cancel')}
          </button>
          <button type="submit" disabled={save.isPending}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
            {save.isPending ? t('properties:enhanced.buildingForm.saving') : t('properties:enhanced.buildingForm.save')}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ── bulk create rooms drawer ──────────────────────────────────────────────────
function BulkCreateRoomsDrawer({ open, building, onClose }) {
  const { t } = useTranslation('properties');
  const qc = useQueryClient();

  const [prefix, setPrefix] = useState('P');
  const [floorFrom, setFloorFrom] = useState(1);
  const [floorTo, setFloorTo] = useState(4);
  const [roomsPerFloor, setRoomsPerFloor] = useState(4);
  const [roomStart, setRoomStart] = useState(1);
  const [monthlyRent, setMonthlyRent] = useState('');
  const [area, setArea] = useState('');
  const [capacity, setCapacity] = useState('');
  const [status, setStatus] = useState('available');

  useEffect(() => {
    if (!open) {
      setPrefix('P'); setFloorFrom(1); setFloorTo(4);
      setRoomsPerFloor(4); setRoomStart(1);
      setMonthlyRent(''); setArea(''); setCapacity(''); setStatus('available');
    }
  }, [open]);

  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const labelCls = 'block text-xs font-medium text-stone-600 mb-1.5';

  // Live preview of rooms to be created
  const preview = useMemo(() => {
    const rooms = [];
    const fFrom = Math.max(1, floorFrom);
    const fTo = Math.max(fFrom, floorTo);
    const rpf = Math.max(1, Math.min(roomsPerFloor, 20));
    const rStart = Math.max(1, roomStart);
    for (let f = fFrom; f <= Math.min(fTo, 20); f++) {
      for (let r = 0; r < rpf; r++) {
        const roomNum = rStart + r;
        const paddedRoom = String(roomNum).padStart(2, '0');
        rooms.push({ floor: f, room_number: `${prefix}${f}${paddedRoom}` });
      }
    }
    return rooms;
  }, [prefix, floorFrom, floorTo, roomsPerFloor, roomStart]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = preview.map(r => ({
        building_id: building?.id,
        room_number: r.room_number,
        floor: r.floor,
        monthly_rent: parseFloat(monthlyRent) || 0,
        area: parseFloat(area) || undefined,
        capacity: parseInt(capacity) || undefined,
        status,
      }));
      // Sequential to avoid overwhelming the API
      const results = [];
      for (const p of payload) {
        results.push(await api.post('/api/properties/rooms', p).then(r => r.data));
      }
      return results;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['rooms', building?.id] });
      qc.invalidateQueries({ queryKey: ['buildings'] });
      qc.invalidateQueries({ queryKey: ['property-stats'] });
      toast.success(t('enhanced.toast.bulkRoomsCreated', { count: data.length }));
      onClose();
    },
    onError: (err) => toast.error(apiErrMsg(err, t('enhanced.toast.saveFailed'))),
  });

  const floorGroups = useMemo(() => {
    const g = {};
    preview.forEach(r => { if (!g[r.floor]) g[r.floor] = []; g[r.floor].push(r.room_number); });
    return g;
  }, [preview]);

  return (
    <Drawer open={open} onClose={onClose}
      title={t('enhanced.bulkCreate.title')}
      subtitle={building?.name}
      width={480}>
      <form onSubmit={e => { e.preventDefault(); save.mutate(); }} className="flex-1 min-h-0 flex flex-col">
        <DrawerBody>
          {/* Naming pattern */}
          <DrawerSection title={t('enhanced.bulkCreate.section.pattern')}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t('enhanced.bulkCreate.field.prefix')}</label>
                  <input className={fieldCls} value={prefix} onChange={e => setPrefix(e.target.value.toUpperCase())} placeholder="P" maxLength={3} />
                  <p className="text-[11px] text-stone-400 mt-1">{t('enhanced.bulkCreate.prefixHint')}</p>
                </div>
                <div>
                  <label className={labelCls}>{t('enhanced.bulkCreate.field.roomStart')}</label>
                  <input type="number" className={fieldCls} value={roomStart} min={1} max={99} onChange={e => setRoomStart(parseInt(e.target.value) || 1)} />
                  <p className="text-[11px] text-stone-400 mt-1">{t('enhanced.bulkCreate.roomStartHint')}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>{t('enhanced.bulkCreate.field.floorFrom')}</label>
                  <input type="number" className={fieldCls} value={floorFrom} min={1} max={50} onChange={e => setFloorFrom(parseInt(e.target.value) || 1)} />
                </div>
                <div>
                  <label className={labelCls}>{t('enhanced.bulkCreate.field.floorTo')}</label>
                  <input type="number" className={fieldCls} value={floorTo} min={floorFrom} max={50} onChange={e => setFloorTo(parseInt(e.target.value) || 1)} />
                </div>
                <div>
                  <label className={labelCls}>{t('enhanced.bulkCreate.field.roomsPerFloor')}</label>
                  <input type="number" className={fieldCls} value={roomsPerFloor} min={1} max={20} onChange={e => setRoomsPerFloor(parseInt(e.target.value) || 1)} />
                </div>
              </div>
            </div>
          </DrawerSection>

          {/* Default room settings — 2×2 compact grid */}
          <DrawerSection title={t('enhanced.bulkCreate.section.defaults')}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{t('enhanced.roomForm.field.monthlyRent')} *</label>
                <CurrencyInput className={fieldCls} value={monthlyRent} onChange={e => setMonthlyRent(e.target.value)} placeholder="4.500.000" />
              </div>
              <div>
                <label className={labelCls}>{t('enhanced.roomForm.field.status')}</label>
                <select className={fieldCls} value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="available">{t('enhanced.roomStatus.available')}</option>
                  <option value="maintenance">{t('enhanced.roomStatus.maintenance')}</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{t('enhanced.roomForm.field.area')}</label>
                <input type="number" step="0.1" className={fieldCls} value={area} min={0} onChange={e => setArea(e.target.value)} placeholder="25" />
              </div>
              <div>
                <label className={labelCls}>{t('enhanced.roomForm.field.capacity')}</label>
                <input type="number" className={fieldCls} value={capacity} min={1} onChange={e => setCapacity(e.target.value)} placeholder="2" />
              </div>
            </div>
          </DrawerSection>

          {/* Live preview */}
          <DrawerSection title={t('enhanced.bulkCreate.section.preview', { count: preview.length })}>
            {preview.length === 0 ? (
              <p className="text-[12px] text-stone-400">{t('enhanced.bulkCreate.noPreview')}</p>
            ) : (
              <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                {Object.entries(floorGroups).map(([floor, roomNums]) => (
                  <div key={floor}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 mb-1">
                      {t('enhanced.floorLabel', { floor })}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {roomNums.map(rn => (
                        <span key={rn} className="px-2 py-0.5 bg-teal-50 border border-teal-200 text-teal-700 text-[12px] font-medium rounded">
                          {rn}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DrawerSection>
        </DrawerBody>

        <DrawerFooter>
          <button type="button" onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            {t('enhanced.roomForm.cancel')}
          </button>
          <button
            type="submit"
            disabled={save.isPending || preview.length === 0 || !monthlyRent}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
            {save.isPending
              ? t('enhanced.bulkCreate.creating')
              : t('enhanced.bulkCreate.create', { count: preview.length })}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ── room form drawer ──────────────────────────────────────────────────────────
function RoomFormDrawer({ open, room, building, onClose }) {
  const { t } = useTranslation('properties');
  const qc = useQueryClient();
  const isEdit = !!room;

  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const labelCls = 'block text-xs font-medium text-stone-600 mb-1.5';

  const save = useMutation({
    mutationFn: (data) =>
      isEdit
        ? api.put(`/api/properties/rooms/${room.id}`, data).then(r => r.data)
        : api.post('/api/properties/rooms', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms', building?.id] });
      qc.invalidateQueries({ queryKey: ['buildings'] });
      qc.invalidateQueries({ queryKey: ['property-stats'] });
      toast.success(isEdit ? t('enhanced.toast.roomUpdated') : t('enhanced.toast.roomCreated'));
      onClose();
    },
    onError: (err) => toast.error(apiErrMsg(err, t('enhanced.toast.saveFailed'))),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      room_number: fd.get('room_number'),
      floor: parseInt(fd.get('floor')) || 1,
      area: parseFloat(fd.get('area')) || undefined,
      capacity: parseInt(fd.get('capacity')) || undefined,
      monthly_rent: parseFloat(fd.get('monthly_rent')),
      status: fd.get('status'),
    };
    if (!isEdit) payload.building_id = building?.id;
    save.mutate(payload);
  };

  return (
    <Drawer open={open} onClose={onClose}
      title={isEdit ? t('enhanced.roomForm.editTitle', { number: room?.room_number }) : t('enhanced.roomForm.addTitle')}
      subtitle={building?.name}
      width={400}>
      <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
        <DrawerBody>
          <DrawerSection title={t('enhanced.roomForm.section.info')}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t('enhanced.roomForm.field.roomNumber')} *</label>
                  <input name="room_number" required className={fieldCls} defaultValue={room?.room_number || ''} placeholder="P101" />
                </div>
                <div>
                  <label className={labelCls}>{t('enhanced.roomForm.field.floor')}</label>
                  <input type="number" name="floor" min="1" className={fieldCls} defaultValue={room?.floor || 1} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t('enhanced.roomForm.field.area')}</label>
                  <input type="number" step="0.1" name="area" min="0" className={fieldCls} defaultValue={room?.area || ''} placeholder="25" />
                </div>
                <div>
                  <label className={labelCls}>{t('enhanced.roomForm.field.capacity')}</label>
                  <input type="number" name="capacity" min="1" className={fieldCls} defaultValue={room?.capacity || ''} placeholder="2" />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t('enhanced.roomForm.field.monthlyRent')} *</label>
                <CurrencyInput name="monthly_rent" required className={fieldCls} defaultValue={room?.monthly_rent || ''} placeholder="4.500.000" />
              </div>
              <div>
                <label className={labelCls}>{t('enhanced.roomForm.field.status')}</label>
                <select name="status" className={fieldCls} defaultValue={room?.status || 'available'}>
                  <option value="available">{t('enhanced.roomStatus.available')}</option>
                  <option value="occupied">{t('enhanced.roomStatus.occupied')}</option>
                  <option value="maintenance">{t('enhanced.roomStatus.maintenance')}</option>
                  <option value="reserved">{t('enhanced.roomStatus.reserved')}</option>
                </select>
              </div>
            </div>
          </DrawerSection>
        </DrawerBody>
        <DrawerFooter>
          <button type="button" onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            {t('enhanced.roomForm.cancel')}
          </button>
          <button type="submit" disabled={save.isPending}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
            {save.isPending ? t('enhanced.roomForm.saving') : t('enhanced.roomForm.save')}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ── building info panel ───────────────────────────────────────────────────────
function BuildingInfoPanel({ building, onEditServices, onEditBilling }) {
  const { t } = useTranslation(['properties', 'settings']);

  if (!building) return null;

  const services = (() => {
    let s = building.services_config;
    if (!s) return [];
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch { return []; } }
    return Array.isArray(s) ? s.filter(sv => sv.is_active !== false) : [];
  })();

  const VND = (n) => new Intl.NumberFormat('vi-VN').format(n ?? 0);

  const paymentMethod = building.payment_method ?? 'pay_first';
  const billingDay = building.billing_day ?? 5;
  const graceDays = building.billing_grace_days ?? 3;

  return (
    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
      {/* Billing agreement row */}
      <div className="px-4 py-3 border-b border-stone-100 bg-stone-50/60 flex items-start sm:items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap text-[12px]">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-[11px] ${
            paymentMethod === 'pay_first'
              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
              : 'bg-amber-100 text-amber-700 border border-amber-200'
          }`}>
            {paymentMethod === 'pay_first' ? t('properties:enhanced.buildingForm.paymentMethod.pay_first') : t('properties:enhanced.buildingForm.paymentMethod.pay_later')}
          </span>
          <span className="text-stone-500">
            {t('properties:enhanced.info.billingDay', { day: billingDay })}
          </span>
          <span className="text-stone-500">
            {t('properties:enhanced.info.graceDays', { days: graceDays })}
          </span>
        </div>
        <button
          onClick={onEditBilling}
          className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium"
        >
          <Icons.Edit size={12} /> {t('properties:enhanced.info.editBilling')}
        </button>
      </div>

      {/* Services pricing */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
            {t('properties:enhanced.info.servicesPricing')}
          </p>
          <button
            onClick={onEditServices}
            className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium"
          >
            <Icons.Edit size={12} /> {t('properties:enhanced.info.editServices')}
          </button>
        </div>
        {services.length === 0 ? (
          <p className="text-[12px] text-stone-400 italic">{t('properties:enhanced.info.noServices')}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
            {services.map(s => (
              <div key={s.service_id} className="flex flex-col px-2.5 py-1.5 rounded-lg bg-stone-50 border border-stone-100 min-w-0">
                <span className="text-[11px] text-stone-500 truncate">{s.name}</span>
                <span className="text-[12px] font-semibold text-teal-700 truncate">
                  {s.is_included
                    ? <span className="text-emerald-600 text-[11px]">{t('settings:service.config.included')}</span>
                    : `${VND(s.unit_price)}/${s.unit}`
                  }
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── bulk price bar ────────────────────────────────────────────────────────────
function BulkPriceBar({ count, onApply, onSelectAll, onDeselectAll, onCancel, totalRooms, saving }) {
  const { t } = useTranslation('properties');
  const [price, setPrice] = useState('');

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-teal-200 shadow-lg px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-semibold text-teal-700">
          {t('enhanced.bulk.selected', { count })}
        </span>
        <button onClick={onSelectAll} className="text-[11px] text-stone-500 hover:text-teal-600 underline">
          {t('enhanced.bulk.selectAll', { total: totalRooms })}
        </button>
        <button onClick={onDeselectAll} className="text-[11px] text-stone-500 hover:text-red-500 underline">
          {t('enhanced.bulk.deselectAll')}
        </button>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <span className="text-[12px] text-stone-500 hidden sm:inline">{t('enhanced.bulk.setRentLabel')}</span>
        <input
          type="number"
          min="0"
          placeholder={t('enhanced.bulk.pricePlaceholder')}
          value={price}
          onChange={e => setPrice(e.target.value)}
          className="w-36 h-8 px-2.5 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
        />
        <button
          onClick={() => price && count > 0 && onApply(parseFloat(price))}
          disabled={!price || count === 0 || saving}
          className="h-8 px-3 rounded-lg bg-teal-600 text-white text-[12px] font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
        >
          {saving ? t('enhanced.bulk.applying') : t('enhanced.bulk.apply', { count })}
        </button>
        <button onClick={onCancel}
          className="h-8 px-3 rounded-lg border border-stone-200 text-stone-600 text-[12px] font-medium hover:bg-stone-50 transition-colors">
          {t('enhanced.bulk.cancel')}
        </button>
      </div>
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────
export default function PropertiesEnhanced() {
  const { t } = useTranslation('properties');
  const qc = useQueryClient();
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expiringDays, setExpiringDays] = useState(30);
  const [search, setSearch] = useState('');
  // building form
  const [showBuildingForm, setShowBuildingForm] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [buildingFormInitialTab, setBuildingFormInitialTab] = useState('info');
  // room form
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  // (building info panel is always visible — no toggle state needed)
  // bulk pricing
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedRoomIds, setSelectedRoomIds] = useState(new Set());

  const bulkUpdateRent = useMutation({
    mutationFn: async ({ roomIds, monthly_rent }) => {
      await Promise.all(roomIds.map(id => api.put(`/api/properties/rooms/${id}`, { monthly_rent }).then(r => r.data)));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms', selectedBuilding?.id] });
      setBulkMode(false);
      setSelectedRoomIds(new Set());
      toast.success(t('enhanced.toast.bulkRentUpdated'));
    },
    onError: (err) => toast.error(apiErrMsg(err, t('enhanced.toast.saveFailed'))),
  });

  const deleteBuilding = useMutation({
    mutationFn: (id) => api.delete(`/api/properties/buildings/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buildings'] });
      qc.invalidateQueries({ queryKey: ['property-stats'] });
      setSelectedBuilding(null);
      toast.success(t('enhanced.toast.buildingDeleted'));
    },
    onError: (err) => toast.error(apiErrMsg(err, t('enhanced.toast.deleteFailed'))),
  });

  const deleteRoom = useMutation({
    mutationFn: (id) => api.delete(`/api/properties/rooms/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms', selectedBuilding?.id] });
      qc.invalidateQueries({ queryKey: ['buildings'] });
      qc.invalidateQueries({ queryKey: ['property-stats'] });
      setSelectedRoom(null);
      toast.success(t('enhanced.toast.roomDeleted'));
    },
    onError: (err) => toast.error(apiErrMsg(err, t('enhanced.toast.deleteFailed'))),
  });

  // ── data ──────────────────────────────────────────────────────────────────────
  const { data: buildings = [], isLoading: buildingsLoading, isError: buildingsError, refetch: refetchBuildings } = useQuery({
    queryKey: ['buildings'],
    queryFn: () => api.get('/api/properties/buildings').then(r => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.buildings ?? []);
    }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: propertyStats } = useQuery({
    queryKey: ['property-stats'],
    queryFn: () => api.get('/api/properties/stats').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ['rooms', selectedBuilding?.id],
    queryFn: () =>
      api.get(`/api/properties/buildings/${selectedBuilding.id}/rooms`).then(r => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.rooms ?? []);
      }),
    enabled: !!selectedBuilding,
    staleTime: 2 * 60 * 1000,
  });

  const { data: contracts = [] } = useQuery({
    queryKey: ['contracts-live'],
    queryFn: async () => {
      // We need both 'active' and 'expiring_soon' contracts to drive the room
      // cards. The backend only accepts ONE status value per call, so we
      // request both in parallel and merge.
      const [active, expiring] = await Promise.all([
        api.get('/api/contracts?status=active').then(r => r.data).catch(() => []),
        api.get('/api/contracts?status=expiring_soon').then(r => r.data).catch(() => []),
      ]);
      const norm = (d) => Array.isArray(d) ? d : (d?.contracts ?? []);
      return [...norm(active), ...norm(expiring)];
    },
    // 30s — contracts changing status (terminated, expired) need to reflect
    // in the room cards quickly. Longer caches caused stale tenants to linger.
    staleTime: 30 * 1000,
  });

  // ── derived ───────────────────────────────────────────────────────────────────
  // From the active-contracts list, classify each contract for its room as
  // either CURRENT (occupying today) or UPCOMING (signed but start_date is in
  // the future). Expired contracts that were never status-updated to
  // 'terminated' are dropped entirely.
  //
  //   Current  → contract.status='active' AND start_date<=today<=end_date
  //   Upcoming → contract.status='active' AND start_date>today
  //
  // When duplicates exist for the same room (shouldn't happen, but legacy
  // data does), prefer the most-recently-started one in each bucket.
  const { contractMap, upcomingMap } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const current = {};
    const upcoming = {};
    // 'active' = currently in effect; 'expiring_soon' = also currently in effect
    // but flagged for renewal attention. Both count as occupying the room.
    const LIVE_STATUSES = new Set(['active', 'expiring_soon']);
    contracts.forEach(c => {
      if (!c.room_id) return;
      if (!LIVE_STATUSES.has(c.status)) return;
      const start = c.start_date ? new Date(c.start_date) : null;
      const end   = c.end_date   ? new Date(c.end_date)   : null;
      if (end && end < today) return;  // expired, status not updated yet

      const names = c.renters?.length
        ? c.renters.map(r => `${r.first_name} ${r.last_name}`).join(', ')
        : c.renter_name || t('enhanced.detail.tenantFallback');
      const info = {
        contractPrice: c.monthly_rent,
        tenantNames: names,
        contractStatus: c.status,
        contractNumber: c.contract_number,
        startDate: start,
        endDate: end,
      };

      const isUpcoming = start && start > today;
      const bucket = isUpcoming ? upcoming : current;
      const existing = bucket[c.room_id];
      // Keep the contract with the later start_date if duplicates exist.
      if (existing && existing.startDate && start && start <= existing.startDate) return;
      bucket[c.room_id] = info;
    });
    return { contractMap: current, upcomingMap: upcoming };
  }, [contracts, t]);

  const filteredRooms = useMemo(() => {
    let list = rooms;
    if (statusFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const threshold = new Date(today);
      threshold.setDate(threshold.getDate() + expiringDays);

      list = list.filter(r => {
        const info = contractMap[r.id];
        const isOccupied = !!info;
        if (statusFilter === 'occupied') return isOccupied;
        if (statusFilter === 'available') return !isOccupied && r.status === 'available';
        if (statusFilter === 'maintenance') return r.status === 'maintenance';
        if (statusFilter === 'expiring') {
          // Active contract whose end_date falls within the next `expiringDays`.
          return isOccupied && info.endDate && info.endDate >= today && info.endDate <= threshold;
        }
        return true;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.room_number?.toLowerCase().includes(q) ||
        contractMap[r.id]?.tenantNames?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [rooms, statusFilter, expiringDays, search, contractMap]);

  const roomsByFloor = useMemo(() => {
    const map = {};
    filteredRooms.forEach(r => {
      const floor = r.floor ?? 1;
      if (!map[floor]) map[floor] = [];
      map[floor].push(r);
    });
    return map;
  }, [filteredRooms]);

  const sortedFloors = useMemo(
    () => Object.keys(roomsByFloor).map(Number).sort((a, b) => a - b),
    [roomsByFloor]
  );

  const buildingStats = useMemo(() => {
    const occupied = rooms.filter(r => !!contractMap[r.id]).length;
    return { total: rooms.length, occupied, available: rooms.filter(r => !contractMap[r.id] && r.status === 'available').length };
  }, [rooms, contractMap]);

  if (buildingsError) {
    return <QueryErrorBanner onRetry={refetchBuildings} className="mt-4" />;
  }

  const FILTER_KEYS = ['all', 'occupied', 'available', 'maintenance', 'expiring'];

  return (
    <div className="flex gap-0 -mx-4 sm:-mx-6 lg:-mx-8 min-h-[calc(100vh-52px)]">
      {/* ── Left: building tree ── */}
      {/* On mobile: full-width when no building selected; hidden when one is selected */}
      {/* On desktop (md+): always shown as a fixed 220px sidebar */}
      <aside className={`flex-shrink-0 border-r border-stone-200 bg-white overflow-y-auto
        ${selectedBuilding ? 'hidden md:flex md:flex-col md:w-[220px]' : 'flex flex-col w-full md:w-[220px]'}`}>
        <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{t('enhanced.buildingsTitle')}</p>
          <button
            onClick={() => { setEditingBuilding(null); setShowBuildingForm(true); }}
            className="flex items-center gap-1 px-2 h-7 rounded-md bg-teal-50 text-teal-700 text-[11px] font-medium hover:bg-teal-100 transition-colors"
            title={t('enhanced.buildingForm.addTitle')}
          >
            <Icons.Plus size={12} /> {t('enhanced.action.add')}
          </button>
        </div>
        <BuildingList
          buildings={buildings}
          selected={selectedBuilding}
          onSelect={(b) => {
            setSelectedBuilding(b);
            setSelectedRoom(null);
            setStatusFilter('all');
            setSearch('');
          }}
          loading={buildingsLoading}
        />
      </aside>

      {/* ── Center: rooms ── */}
      {/* On mobile: hidden when no building selected (building list takes full width) */}
      <div className={`flex-1 min-w-0 overflow-y-auto ${!selectedBuilding ? 'hidden md:block' : ''}`}>
        {/* Mobile back button — tap to return to building list */}
        {selectedBuilding && (
          <button
            className="md:hidden w-full flex items-center gap-2 px-4 py-3 border-b border-stone-100 bg-stone-50 text-teal-700 text-[13px] font-medium hover:bg-stone-100 transition-colors"
            onClick={() => { setSelectedBuilding(null); setSelectedRoom(null); }}
          >
            <Icons.ChevronLeft size={16} />
            {t('enhanced.buildingsTitle')}
          </button>
        )}

        {/* KPI strip (global, always visible) */}
        {propertyStats && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-stone-100 bg-stone-50/50">
            {/* 2-col grid on mobile, 4-col strip on sm+ */}
            <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-0">
              <KPI label={t('enhanced.kpi.totalRooms')}    value={String(propertyStats.total_rooms ?? 0)}    icon={<Icons.Home />}        accent="neutral" />
              <KPI label={t('enhanced.kpi.occupied')}      value={String(propertyStats.occupied_rooms ?? 0)} icon={<Icons.Users />}       accent="success" />
              <KPI label={t('enhanced.kpi.available')}     value={String(propertyStats.available_rooms ?? 0)}icon={<Icons.CheckCircle2 />} accent="warning" />
              <KPI label={t('enhanced.kpi.occupancyRate')} value={`${propertyStats.occupancy_rate ?? 0}%`}   icon={<Icons.Chart />}       accent="primary" />
            </div>
          </div>
        )}

        {/* Building config — always visible below KPI when a building is selected */}
        {selectedBuilding && (
          <div className="px-4 sm:px-6 py-3 border-b border-stone-100">
            <BuildingInfoPanel
              building={selectedBuilding}
              onEditServices={() => { setEditingBuilding(selectedBuilding); setBuildingFormInitialTab('services'); setShowBuildingForm(true); }}
              onEditBilling={() => { setEditingBuilding(selectedBuilding); setBuildingFormInitialTab('billing'); setShowBuildingForm(true); }}
            />
          </div>
        )}

        {!selectedBuilding ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <EmptyState
              icon={<Icons.Building />}
              title={t('enhanced.selectBuilding.title')}
              description={t('enhanced.selectBuilding.description')}
            />
          </div>
        ) : (
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {/* Building header */}
            <div className="space-y-2">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">{selectedBuilding.name}</h2>
                <p className="text-sm text-stone-500 mt-0.5">
                  {t('enhanced.occupiedSummary', { occupied: buildingStats.occupied, total: buildingStats.total })}
                </p>
              </div>
              {/* Action buttons — scrollable row on mobile */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1 -mb-1 scrollbar-none">
                {/* Building management actions */}
                <button
                  onClick={() => { setEditingBuilding(selectedBuilding); setBuildingFormInitialTab('info'); setShowBuildingForm(true); }}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 h-8 rounded-lg border border-stone-200 text-[12px] font-medium text-stone-600 hover:bg-stone-50 transition-colors"
                >
                  <Icons.Edit size={13} /> {t('enhanced.action.editBuilding')}
                </button>
                <button
                  onClick={() => { setEditingRoom(null); setShowRoomForm(true); }}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 h-8 rounded-lg bg-teal-600 text-white text-[12px] font-medium hover:bg-teal-700 transition-colors"
                >
                  <Icons.Plus size={13} /> {t('enhanced.action.addRoom')}
                </button>
                <button
                  onClick={() => setShowBulkCreate(true)}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 h-8 rounded-lg border border-teal-300 text-teal-700 text-[12px] font-medium hover:bg-teal-50 transition-colors"
                >
                  <Icons.Plus size={13} /> {t('enhanced.action.bulkCreateRooms')}
                </button>
                <button
                  onClick={() => {
                    setBulkMode(v => { if (v) setSelectedRoomIds(new Set()); return !v; });
                  }}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 h-8 rounded-lg border text-[12px] font-medium transition-colors ${
                    bulkMode
                      ? 'border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <Icons.DollarSign size={13} /> {t('enhanced.action.bulkPrice')}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(t('enhanced.action.confirmDeleteBuilding', { name: selectedBuilding.name })))
                      deleteBuilding.mutate(selectedBuilding.id);
                  }}
                  disabled={deleteBuilding.isPending}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 h-8 rounded-lg border border-red-200 text-[12px] font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Icons.Trash size={13} />
                </button>
                <div className="w-px h-5 bg-stone-200" />
                {/* Status filter */}
                {FILTER_KEYS.map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-2.5 h-8 rounded-lg text-[12px] font-medium transition-colors ${
                      statusFilter === s
                        ? 'bg-teal-600 text-white'
                        : 'border border-stone-200 text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {t(`enhanced.filter.${s}`)}
                    {s === 'expiring' && statusFilter === 'expiring' && (
                      <span className="ml-1.5 text-[10px] opacity-80">
                        ({t('enhanced.filter.expiringDays', { count: expiringDays })})
                      </span>
                    )}
                  </button>
                ))}
                {statusFilter === 'expiring' && (
                  <select
                    value={expiringDays}
                    onChange={e => setExpiringDays(Number(e.target.value))}
                    className="h-8 px-2 rounded-lg border border-stone-200 text-[12px] text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                    title={t('enhanced.filter.expiringWindow')}
                  >
                    <option value={7}>7 {t('enhanced.filter.daysShort')}</option>
                    <option value={30}>30 {t('enhanced.filter.daysShort')}</option>
                    <option value={60}>60 {t('enhanced.filter.daysShort')}</option>
                    <option value={90}>90 {t('enhanced.filter.daysShort')}</option>
                  </select>
                )}
                {/* Search */}
                <div className="relative">
                  <Icons.Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('enhanced.searchRoom')}
                    className="h-8 pl-7 pr-3 rounded-lg border border-stone-200 text-[12px] placeholder:text-stone-400 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 w-32"
                  />
                </div>
              </div>
            </div>

            {/* Bulk mode hint */}
            {bulkMode && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-700">
                <Icons.DollarSign size={14} className="flex-shrink-0" />
                {t('enhanced.bulk.hint')}
              </div>
            )}

            {/* Rooms by floor */}
            {roomsLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-28 bg-stone-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredRooms.length === 0 ? (
              <EmptyState
                icon={<Icons.Home />}
                title={t('enhanced.noRooms.title')}
                description={t('enhanced.noRooms.description')}
              />
            ) : (
              <div className="space-y-5">
                {sortedFloors.map(floor => (
                  <div key={floor}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
                        {t('enhanced.floorLabel', { floor })}
                      </span>
                      <span className="text-[11px] text-stone-400">
                        {t('enhanced.occupiedShort', {
                          occupied: roomsByFloor[floor].filter(r => contractMap[r.id]).length,
                          total: roomsByFloor[floor].length,
                        })}
                      </span>
                      <div className="flex-1 h-px bg-stone-100" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {roomsByFloor[floor].map(room => (
                        <RoomCard
                          key={room.id}
                          room={room}
                          contractInfo={contractMap[room.id]}
                          upcomingInfo={upcomingMap[room.id]}
                          selected={selectedRoom?.id === room.id}
                          onClick={() => setSelectedRoom(room)}
                          bulkMode={bulkMode}
                          bulkSelected={selectedRoomIds.has(room.id)}
                          onBulkToggle={() => setSelectedRoomIds(prev => {
                            const next = new Set(prev);
                            if (next.has(room.id)) next.delete(room.id); else next.add(room.id);
                            return next;
                          })}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Right: room detail drawer ── */}
      <Drawer
        open={!!selectedRoom}
        onClose={() => setSelectedRoom(null)}
        title={selectedRoom ? t('enhanced.detail.roomTitle', { number: selectedRoom.room_number }) : ''}
        subtitle={
          selectedRoom && selectedBuilding
            ? `${selectedBuilding.name}${selectedRoom.floor ? ` · ${t('enhanced.floorLabel', { floor: selectedRoom.floor })}` : ''}`
            : undefined
        }
        width={480}
      >
        <RoomDetailDrawer
          room={selectedRoom}
          building={selectedBuilding}
          onClose={() => setSelectedRoom(null)}
          onEdit={() => { setEditingRoom(selectedRoom); setSelectedRoom(null); setShowRoomForm(true); }}
          onDelete={() => {
            if (window.confirm(t('enhanced.action.confirmDeleteRoom', { number: selectedRoom?.room_number })))
              deleteRoom.mutate(selectedRoom.id);
          }}
        />
      </Drawer>

      {/* Bulk price action bar */}
      {bulkMode && (
        <BulkPriceBar
          count={selectedRoomIds.size}
          totalRooms={filteredRooms.length}
          saving={bulkUpdateRent.isPending}
          onApply={(price) => bulkUpdateRent.mutate({ roomIds: [...selectedRoomIds], monthly_rent: price })}
          onSelectAll={() => setSelectedRoomIds(new Set(filteredRooms.map(r => r.id)))}
          onDeselectAll={() => setSelectedRoomIds(new Set())}
          onCancel={() => { setBulkMode(false); setSelectedRoomIds(new Set()); }}
        />
      )}

      {/* Building form drawer */}
      <BuildingFormDrawer
        open={showBuildingForm}
        building={editingBuilding}
        initialTab={buildingFormInitialTab}
        onClose={() => { setShowBuildingForm(false); setEditingBuilding(null); setBuildingFormInitialTab('info'); }}
        onSaved={() => {
          if (editingBuilding && selectedBuilding?.id === editingBuilding.id) {
            qc.invalidateQueries({ queryKey: ['buildings'] });
          }
        }}
      />

      {/* Room form drawer */}
      <RoomFormDrawer
        open={showRoomForm}
        room={editingRoom}
        building={selectedBuilding}
        onClose={() => { setShowRoomForm(false); setEditingRoom(null); }}
      />

      {/* Bulk create rooms drawer */}
      <BulkCreateRoomsDrawer
        open={showBulkCreate}
        building={selectedBuilding}
        onClose={() => setShowBulkCreate(false)}
      />
    </div>
  );
}
